import React, { useState } from 'react';
import { Task } from '@models/task.model';
import { Card } from '@components/atoms/Card';
import { Badge } from '@components/atoms/Badge';
import { Avatar } from '@components/atoms/Avatar';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';

interface TaskDetailCrewsProps {
  task: Task | null;
}

const formatRole = (role?: string | null) => {
  if (!role) return '';
  return role
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

export const TaskDetailCrews: React.FC<TaskDetailCrewsProps> = ({ task }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const crews = task?.crews || [];

  if (crews.length === 0) {
    return null;
  }

  return (
    <Card className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Crews
          </h3>
          <Badge variant="secondary" size="sm">
            {crews.length}
          </Badge>
        </div>

        <div
          className="cursor-pointer"
          onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <ExpandMoreOutlinedIcon /> : <ExpandLessOutlinedIcon />}
        </div>
      </div>

      {!isCollapsed && (
        <div className="flex flex-col gap-4">
          {crews.map((crew) => {
            const members = crew.employees || [];
            return (
              <div key={crew.crewId} className="flex flex-col gap-2">
                <div className="flex items-center justify-between border-b border-gray-100 pb-1">
                  <span className="text-sm font-semibold text-gray-800">
                    {crew.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {members.length} {members.length === 1 ? 'member' : 'members'}
                  </span>
                </div>

                {members.length === 0 ? (
                  <p className="text-xs italic text-gray-400">
                    No members assigned
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {members.map((employee) => {
                      const user = employee.user;
                      const fullName =
                        `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
                        'Unknown';
                      return (
                        <li
                          key={employee.employeeId}
                          className="flex items-center gap-3">
                          <Avatar
                            size="sm"
                            src={user?.avatar}
                            text={fullName}
                            alt={fullName}
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {fullName}
                            </span>
                            {user?.role && (
                              <span className="text-xs text-gray-500 truncate">
                                {formatRole(user.role)}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

export default TaskDetailCrews;
